const STORAGE_KEY = 'signup_attribution';

// Au-delà, l'attribution est considérée périmée (fenêtre large vs les 7 jours
// du clic META) : on ne rejoue pas un fbclid vieux de plusieurs mois.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// ⚠️ À garder synchronisé avec :
// - unboared-landing/src/components/tracking/AttributionTracker.tsx (TRACKED_PARAMS)
// - stripe-b2b/server.js (ATTRIBUTION_FIELDS)
export const ATTRIBUTION_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];

/**
 * Persiste les paramètres d'acquisition (UTM + fbclid, propagés par unboared.com)
 * pour qu'ils survivent au parcours multi-étapes signup → infos → Stripe checkout.
 * Ils finissent en metadata du customer Stripe (CAC réel par campagne).
 * Appelé au bootstrap (main.tsx) pour couvrir toutes les portes d'entrée
 * (/signup, /demo, redirections d'utilisateur déjà connecté...).
 */
export function captureAttribution() {
    try {
        const params = new URLSearchParams(window.location.search);
        const incoming: Record<string, string> = {};
        ATTRIBUTION_PARAMS.forEach((key) => {
            const value = params.get(key);
            if (value) incoming[key] = value;
        });
        if (Object.keys(incoming).length === 0) return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...getAttribution(), ...incoming, _ts: Date.now() }));
    } catch {
        // localStorage indisponible (Safari privé) : on perd l'attribution, jamais le signup
    }
}

export function getAttribution(): Record<string, string> {
    try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        const ts = Number(stored._ts);
        if (!ts || Date.now() - ts > MAX_AGE_MS) return {};
        // Allowlist stricte : le contenu part en champs cachés du form checkout,
        // une clé parasite (ex. 'userID') dupliquerait un champ métier.
        const attribution: Record<string, string> = {};
        ATTRIBUTION_PARAMS.forEach((key) => {
            if (typeof stored[key] === 'string') attribution[key] = stored[key];
        });
        return attribution;
    } catch {
        return {};
    }
}

/**
 * Cookie `_fbc` posé par le pixel META quand fbclid est dans l'URL : il porte
 * le vrai timestamp du clic d'ad, contrairement à un fbc resynthétisé à J+14.
 */
export function getFbcCookie(): string | undefined {
    const match = document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Cookie `_fbp` (identifiant navigateur du pixel META) : envoyé au checkout pour
 * que l'événement serveur (Conversions API, stripe-b2b metaCapi.js) soit rattaché
 * au même navigateur que les événements du pixel.
 */
export function getFbpCookie(): string | undefined {
    const match = document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : undefined;
}

/** Faux seulement si l'animateur a refusé la publicité dans le bandeau (index.html `__ubAdsConsent`). */
export function adsAllowed(): boolean {
    const check = (window as any).__ubAdsConsent;
    return !check || check();
}

/**
 * Déclenche un event Meta Pixel. index.html ne charge pas le pixel sur les pages
 * joueurs et écrans (/gamepad, /reward, /app) : si la session a démarré sur l'une
 * d'elles, on le charge ici, pour ne jamais perdre une conversion du tunnel.
 */
export function trackMetaPixel(
    eventName: string,
    { custom = false, eventID }: { custom?: boolean; eventID?: string } = {}
) {
    // Refus de la publicité dans le bandeau (index.html `__ubAdsConsent`) : rien ne part,
    // même si le pixel avait été chargé avant le refus sur cette page.
    if (!adsAllowed()) return;
    if (!(window as any).fbq) (window as any).__loadMetaPixel?.();
    const fbq = (window as any).fbq;
    if (!fbq) return;
    // eventID : identique à l'event_id envoyé par le serveur (Conversions API)
    // → Meta compte une seule conversion au lieu de deux.
    if (eventID) fbq(custom ? 'trackCustom' : 'track', eventName, {}, { eventID });
    else fbq(custom ? 'trackCustom' : 'track', eventName);
}
